// src/components/AiDashboard/MyNutritionStatus.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker, { getRangeLabel } from "../nutrition/DateRangePicker";
import "./MyNutritionStatus.css";
import "../../App.css";
import "../nutrition/fitnessbodycomp/nutritionFitnessBodyComp.css";
import { useAuth } from "../../firebase/AuthContext";
import TwoRingDonutChart from "./charts/PieChart";
import { Card, CardContent, ProgressRing, Tile } from "../nutrition/nutrition-ui-kit";
import { CiMedicalClipboard } from "react-icons/ci";
import { FcEngineering, FcCalendar, FcAssistant, FcAnswers } from "react-icons/fc";
import { IoCopyOutline, IoCheckmarkOutline } from "react-icons/io5";
import {
  getChartData,
  uiText,
  statusToBadge,
  allNutrientOptions,
  getStatuses,
  getCategoryOverview,
  getGroups,
  buildTileSpec,
  getCategoryForId,
  SKIP_TRIAL_REDIRECT,
  DV_OK_LOW,
  DV_OK_HIGH,
  buildNutritionDataOverridesFromCalculateSnapshot,
} from "../nutrition/nutritionData";
import { useFirestoreNutrition } from "../nutrition/useFirestoreNutrition";
import AiUserSettings from "./AiMyNutritionStatus/AiUser/AiUserSettings/AiUserSettings";
import { db } from "../../firebase/firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import {
  buildNutritionTargetsSettingsPreview,
  buildUseDailyPreviewFromNnr2023,
} from "./AiMyNutritionStatus/AiUser/AiUserSettings/nutritionTargetsSettings.ts";
import { buildNutritionTargetsCalculationsPreview } from "./AiMyNutritionStatus/AiUser/AiUserSettings/nutritionTargetsCalculations.ts";
import AiMyNutritionStatus from "./AiMyNutritionStatus";
import sourcesCsvUrl from "./sources.csv";
import AlertModal from "../payments/server/alertModal";
import { NutrientTrendAnalyzer } from "../nutrition/NutrientTrendAnalyzer.ts";
import { buildBmiNodeFromSnapshots } from "../nutrition/fitnessbodycomp/nutritionFitnessBodyComp";

// 🔧 Feature flag to show/hide the dev JSON button and modal
// Set this to true when you want to enable the button again.
const IS_DEV_JSON_BUTTON_VISIBLE = false;

const KNOWN_NUTRIENT_IDS = new Set(
  (allNutrientOptions || []).map((option) => option.id),
);

const SNAPSHOT_TO_TILE_ID_OVERRIDES = Object.freeze({
  fat_g: "totalFat",
  saturates_g: "sfa",
  pufa_g: "pufa",
  mufa_g: "mufa",
  n3_pufa_g: "n3Total",
  ala_g: "ala",
  tfa_g: "tfa",
});

const MIN_DAYS_FOR_META_ICON = 10;

const BMI_INFO_MESSAGE = `The **BMI & Body Fat** tile is a compact weight-loss coach. It combines your body measurements and logged food intake to show where you are today, how your current pattern affects your weight over time, and what a safe, realistic plan could look like for you.

**1. Where you are right now**

• At the top of the tile, you see your **current BMI** and **BMI category** (for example, "BMI 24.9 – Normal weight") along with your **current weight** and an **estimated body fat percentage**.
• The "Vs NNR ref." line compares your weight to a life-stage reference weight based on Nordic Nutrition Recommendations (NNR), so you can see roughly how many kilos above or below that reference you are.
• This gives you a clear, objective snapshot of your body composition rather than focusing only on the scale.

**2. Your energy needs vs. how much you log**

• The tile shows your **estimated daily energy need** (in kcal/day), calculated from your profile and activity pattern. This is a personalized estimate of what you would need to roughly maintain your current weight.
• It then compares that to your **average logged intake** over the selected date range (for example, a 30-day average, counting only days where you actually logged food).
• From these two numbers it calculates your **energy balance** – roughly how many calories per day you are in **deficit** (eating less than you need) or **surplus** (eating more than you need). A large negative number means a strong deficit; a small negative number means a gentle deficit.

**3. What happens if this pattern continues**

• Using the estimated daily deficit or surplus, the tile derives a **theoretical weekly weight change** (for example, "~1.5 kg/week loss if this pattern continued").
• This is not a promise, but a physics-based estimate: it assumes that a certain amount of calories correspond to roughly one kilogram of body weight.
• It helps you see whether your current pattern is closer to **maintain**, **gentle loss**, or **very aggressive loss**, and reminds you that very rapid loss is often a sign of under-logging or an approach that may not be sustainable.

**4. Is this a "healthy" deficit? Macros and nutrients**

• The tile also summarises your **average intake of energy, protein, fat, carbohydrates, and fibre** as a percentage of your targets over the selected range (for example, "Energy 46%, Protein 80%, Fat 66%, Carbs 26%, Fibre 33% of target").
• This shows whether you are cutting calories in a way that still supports **muscle, satiety, and gut health**. Ideally, protein and fibre stay close to 100% even when total energy is lower.
• The **"Nutrient priorities in deficit"** line highlights key vitamins and minerals that are most below target on your logged days (for example, Fluoride, Molybdenum, Iodine, Calcium). This is there to nudge you toward **nutrient-dense foods** instead of just eating less.

**5. Your weight-loss goal and plan**

• If you have set a explicit weight-loss goal, the tile converts it into a clear, numeric target (for example, "Lose ~5 kg to ~90.3 kg").
• Based on your goal and energy needs, it estimates an **indicative timeline** (for example, "about 10 weeks at ~0.5 kg/week") that sits inside a typical safe range.
• It also suggests an **indicative energy plan** (for example, "~2151 kcal/day, about 550 kcal/day below your estimated needs") so you have a concrete ballpark to aim for rather than guessing.
• The **SMART plan** line turns this into an action-oriented summary: what to aim for per day, how long to keep it up, and how to protect protein, fibre and key nutrients while you are in a deficit.
• Finally, **"Suggested focus"** gives you a simple headline like "Focus on losing about 5 kg in sustainable steps" so you always know the main priority.

**6. How to use this in practice**

• Treat this tile as a **compass**, not a verdict: it shows how your current logging and goals line up with a realistic, safe weight-loss path.
• If the estimated deficit is extremely large or the projected weekly loss is very high, it may be a sign that you are under-logging or pushing too hard, and the text will call that out.
• If the deficit is in a moderate range and your protein, fibre, and key nutrients are reasonably close to target, the tile will reinforce that you are on a more **balanced, sustainable track**.
• Because all estimates depend on what you log, the most important action is to **log every intake as honestly as possible**. Better logging makes the BMI & Body Fat card—and all the weight-loss guidance attached to it—more accurate and more useful over time.

This explanation is intended to help you understand what the card is showing, why certain numbers (like the deficit, projected trend, and timeline) look the way they do, and how to use them to guide a safe, sustainable weight-loss plan rather than chasing quick fixes.`;

const BMI_INFO_SECTIONS = [
  {
    id: "snapshot",
    index: "1",
    title: "Where you are right now",
    variant: "snapshot",
    bullets: [
      "At the top of the tile you see your current BMI and BMI category (for example “BMI 24.9 – Normal weight”), your current weight and an estimated body-fat percentage.",
      "The “Vs NNR ref.” line compares your weight to a life-stage reference weight from the Nordic Nutrition Recommendations so you can see roughly how many kilos above or below that reference you are.",
      "The goal is to give a clear, objective snapshot of your body composition instead of focusing only on the scale.",
    ],
  },
  {
    id: "energy",
    index: "2",
    title: "Your energy needs vs. how much you log",
    variant: "energy",
    bullets: [
      "The card shows your estimated daily energy need in kcal/day based on your profile and activity pattern – roughly what you would need to maintain your current weight.",
      "It compares that to your average logged intake over the selected range (for example a 30-day average, counting only days where you logged food).",
      "From those two numbers it derives your energy balance: how many calories per day you are in deficit (eating less) or surplus (eating more). Large negative numbers mean a strong deficit; smaller gaps are gentler.",
    ],
  },
  {
    id: "trend",
    index: "3",
    title: "What happens if this pattern continues",
    variant: "trend",
    bullets: [
      "Using the daily deficit or surplus, the tile estimates a theoretical weekly weight change (for example “~1.5 kg/week loss if this pattern continued”).",
      "This is a physics-based estimate, not a promise: it assumes a typical energy-to-weight conversion and steady behaviour over time.",
      "It helps you see whether your current pattern looks like maintenance, gentle loss or very aggressive loss – and calls out patterns that are unlikely to be sustainable or may indicate under-logging.",
    ],
  },
  {
    id: "nutrients",
    index: "4",
    title: "Is this a healthy deficit? Macros and nutrients",
    variant: "nutrients",
    bullets: [
      "The card summarises your average intake of energy, protein, fat, carbohydrates and fibre as % of target over the selected range.",
      "Healthy loss usually means keeping protein and fibre close to target even when total energy is lower, to support muscle, satiety and gut health.",
      "The “Nutrient priorities in deficit” line highlights vitamins and minerals that are most below target on your logged days so you can lean toward nutrient-dense foods, not just “less food”.",
    ],
  },
  {
    id: "goal",
    index: "5",
    title: "Your weight-loss goal and plan",
    variant: "goal",
    bullets: [
      "If you set a weight-loss goal, the card translates it into a numeric target (for example “Lose ~5 kg to ~90.3 kg”).",
      "From your energy needs and chosen deficit it estimates a rough timeline (for example “about 10 weeks at ~0.5 kg/week”) that sits inside a typical safe range.",
      "It suggests an indicative daily energy plan (for example “~2150 kcal/day, about 550 kcal/day below your estimated needs”) so you have a concrete ball-park instead of guessing.",
      "The SMART-style summary explains what to aim for per day, for how long, and how to protect protein, fibre and key nutrients while you are in a deficit.",
      "It is recommended you check your weight every day, then after a week update your weight in the settings so we can continue tailoring to your goal.",
      "A short “Suggested focus” headline keeps the main priority visible (for example “Focus on losing about 5 kg in sustainable steps”).",
    ],
  },
  {
    id: "usage",
    index: "6",
    title: "How to use this in practice",
    variant: "usage",
    bullets: [
      "Treat the BMI & Body Fat tile as a compass, not a verdict: it shows how your logging and goals line up with a safe, realistic path rather than judging you.",
      "Very large deficits or extreme projected weekly losses can point to under-logging or pushing too hard; balanced deficits with decent protein, fibre and micronutrients suggest a more sustainable track.",
      "The single most important action is to log everything you actually eat as honestly as you can – better logging makes the card’s estimates and guidance more accurate over time.",
    ],
  },
];

function BmiInfoAnswersheet() {
  return (
    <div
      className="bmi-info-modal"
      data-bmi-markdown-template={BMI_INFO_MESSAGE}
    >
      <p className="bmi-info-intro">
        The BMI &amp; Body Fat tile is a compact weight-loss coach. It combines your
        body measurements and logged food intake to show where you are today, how
        your current pattern affects your weight over time, and what a safe,
        realistic plan could look like for you.
      </p>

      {BMI_INFO_SECTIONS.map((section) => (
        <section
          key={section.id}
          className={`bmi-info-section bmi-info-section--${section.variant}`}
          aria-label={section.title}
        >
          <header className="bmi-info-section-header">
            <span className="bmi-info-section-index">{section.index}.</span>
            <h4 className="bmi-info-section-title">{section.title}</h4>
          </header>

          <ul className="bmi-info-list">
            {section.bullets.map((bulletPoint) => (
              <li key={bulletPoint.slice(0, 40)}>{bulletPoint}</li>
            ))}
          </ul>
        </section>
      ))}

      <p className="bmi-info-footnote">
        This explanation is there to help you understand what the card is showing,
        why numbers like the deficit, projected trend and timeline look the way
        they do, and how to use them to support a safe, sustainable weight-loss
        plan instead of chasing quick fixes.
      </p>
    </div>
  );
}


function buildPriorityInfoMessage(rangeLabel) {
  const effectiveRangeLabel = rangeLabel || "your selected dates";

  return [
    `The Priority nutrients section shows the vitamins, minerals and fats that need the most attention for ${effectiveRangeLabel}. It looks at your logged intake and surfaces nutrients that are low or consistently below target so you can see where your diet is thinnest right now.`,
    "",
    "Each tile is a focused task: it summarises your status, shows how far you are from your daily value and, where possible, gives quick food ideas you can add to upcoming meals. Use this section as a short to-do list for your next days of logging by picking one or two nutrients, adding the suggested foods, and watching how those tiles improve over time."
  ].join("\n\n");
}


function NutrientAlertModalBody({
  nutrientLabel,
  direction,
  message,
  showClipboardExplanation,
  minDaysForClipboard,
}) {
  const directionLabel =
    direction === "high"
      ? "High intake pattern (toxicity risk)"
      : "Possible deficiency pattern";

  return (
    <div className="phn-modal-body">
      <header className="phn-modal-header">
        <h4 className="phn-modal-heading">
          <span className="phn-modal-heading-text">{nutrientLabel}</span>
          <span
            className={
              direction === "high"
                ? "phn-modal-chip phn-modal-chip--high"
                : "phn-modal-chip phn-modal-chip--low"
            }
          >
            {directionLabel}
          </span>
        </h4>
      </header>

      {showClipboardExplanation && (
        <section className="phn-modal-section phn-modal-section--clipboard">
          <h5 className="phn-modal-section-title">
            Why this clipboard icon appears
          </h5>
          <p className="phn-modal-section-text">
            We only show the clipboard icon on nutrients where your logged data
            clearly stands out for example several days in a row well below or
            above target, or a single very extreme day and when there are at
            least {minDaysForClipboard} logged days in the selected period.
            Tiles without the clipboard either look broadly on track, or do not
            yet have enough data for a reliable pattern.
          </p>
        </section>
      )}

      <section className="phn-modal-section">
        <h5 className="phn-modal-section-title">What we are seeing</h5>
        <p className="phn-modal-section-text">
          {message}
        </p>
      </section>

      <section className="phn-modal-section phn-modal-section--hint">
        <h5 className="phn-modal-section-title">How to use this</h5>
        <p className="phn-modal-section-text">
          Use this as a prompt to adjust upcoming meals, review any
          supplements, and, if you are concerned or notice symptoms, discuss
          the pattern with a healthcare professional. Clipboard tiles are
          meant to be a short, data-driven checklist of things worth extra
          attention for this period.
        </p>
      </section>
    </div>
  );
}

function mapSnapshotNutrientIdToTileId(rawId) {
  if (!rawId || typeof rawId !== "string") {
    return rawId;
  }

  if (KNOWN_NUTRIENT_IDS.has(rawId)) {
    return rawId;
  }

  const overrideTileId = SNAPSHOT_TO_TILE_ID_OVERRIDES[rawId];
  if (overrideTileId && KNOWN_NUTRIENT_IDS.has(overrideTileId)) {
    return overrideTileId;
  }

  const withoutSuffix = rawId.replace(
    /_(µg_re|µg|mg|g|ne|ate|kcal|kj)$/i,
    "",
  );

  if (KNOWN_NUTRIENT_IDS.has(withoutSuffix)) {
    return withoutSuffix;
  }

  const vitaminMatch = withoutSuffix.match(/^(vitamin[a-z0-9]+)/i);
  if (vitaminMatch) {
    const vitaminCandidate = vitaminMatch[1];
    if (KNOWN_NUTRIENT_IDS.has(vitaminCandidate)) {
      return vitaminCandidate;
    }
  }

  return rawId;
}

function formatDateToIsoDateString(date) {
  if (!(date instanceof Date)) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function getAiAssistantUrl() {
  if (typeof window === "undefined") {
    return "/ai-assistant";
  }

  const isDevEnvironment =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  return isDevEnvironment
    ? "http://localhost:3000/ai-assistant"
    : "https://purehealthnexus.com/ai-assistant";
}

function normaliseVitaminKeyFromLabel(label) {
  if (!label || typeof label !== "string") {
    return null;
  }

  return label
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/\s*\(.*?\)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function atStartOfDay(date) {
  if (!(date instanceof Date)) {
    return null;
  }
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function getInclusiveDaySpan(start, end) {
  const startAtMidnight = atStartOfDay(start);
  const endAtMidnight = atStartOfDay(end);

  if (!startAtMidnight || !endAtMidnight) {
    return null;
  }

  const startMs = startAtMidnight.getTime();
  const endMs = endAtMidnight.getTime();

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return null;
  }

  if (endMs < startMs) {
    return 0;
  }

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const differenceMs = endMs - startMs;
  return Math.floor(differenceMs / millisecondsPerDay) + 1;
}

function describeTrendWindows(windows) {
  if (!Array.isArray(windows) || windows.length === 0) {
    return "over the selected dates";
  }

  if (windows.includes("180d")) {
    return "over roughly the last 6 months";
  }

  if (windows.includes("90d")) {
    return "over roughly the last 3 months";
  }

  if (windows.includes("10d")) {
    return "over many of the last 10 logged days";
  }

  return "over the selected dates";
}

function deriveCategoryOverviewStatusKey(category, mainStat, subStat, statuses) {
  if (!category) {
    return "data-needed";
  }

  const categoryId =
    typeof category.id === "string" ? category.id.trim() : "";

  if (categoryId === "body" || categoryId === "bodyComp" || categoryId === "bmi") {
    const bmiStatus =
      typeof statuses?.bmi === "string" && statuses.bmi.trim().length > 0
        ? statuses.bmi.trim()
        : null;

    if (bmiStatus) {
      return bmiStatus;
    }
  }

  if (categoryId === "fats") {
    const normalizedSub = (subStat || "").toLowerCase();

    if (normalizedSub.includes("above")) {
      return "high";
    }
    if (normalizedSub.includes("below")) {
      return "low";
    }
    if (normalizedSub.includes("within")) {
      return "on-track";
    }
  }

  const percentMatch =
    typeof mainStat === "string" ? mainStat.match(/(\d+(\.\d+)?)\s*%/) : null;

  const averagePercent = percentMatch
    ? Number.parseFloat(percentMatch[1])
    : null;

  const hasLow =
    typeof subStat === "string" ? /(\d+)\s*low/i.test(subStat) : false;

  const hasHighOrCaution =
    typeof subStat === "string" ? /(high|caution)/i.test(subStat) : false;

  if (averagePercent == null && !hasLow && !hasHighOrCaution) {
    return "data-needed";
  }

  if (averagePercent != null && Number.isFinite(averagePercent)) {
    if (averagePercent < DV_OK_LOW) {
      if (hasHighOrCaution) {
        return "off-track";
      }
      return "low";
    }

    if (averagePercent > DV_OK_HIGH) {
      if (hasLow) {
        return "off-track";
      }
      return "high";
    }
  }

  if (hasLow && hasHighOrCaution) {
    return "caution";
  }

  if (hasLow) {
    return "low";
  }

  if (hasHighOrCaution) {
    return "high";
  }

  return "on-track";
}

function shouldIncludeRuleSummaryInMessage(alert, daysInSelectedRange) {
  if (!alert || !Array.isArray(alert.windows) || !alert.ruleSummary) {
    return false;
  }

  if (!Number.isFinite(daysInSelectedRange) || daysInSelectedRange < 10) {
    return false;
  }

  const hasSufficientWindow = alert.windows.some((windowId) => {
    const match = typeof windowId === "string" && windowId.match(/^(\d+)d$/i);
    if (!match) {
      return false;
    }
    const windowDays = Number.parseInt(match[1], 10);
    if (!Number.isFinite(windowDays)) {
      return false;
    }
    return windowDays >= 10;
  });

  return hasSufficientWindow;
}

function buildDailyPercentDvFromDailySnapshots(dailyIntakeByDate) {
  const result = {};

  if (!dailyIntakeByDate || typeof dailyIntakeByDate !== "object") {
    return result;
  }

  Object.entries(dailyIntakeByDate).forEach(([dateIso, documentData]) => {
    if (!documentData || typeof documentData !== "object") {
      return;
    }

    const comparisonFromRoot =
      documentData.intakeSummary?.comparisonVsTargets ||
      documentData.comparisonVsTargets ||
      null;

    if (!comparisonFromRoot || typeof comparisonFromRoot !== "object") {
      return;
    }

    Object.entries(comparisonFromRoot).forEach(([nutrientId, comparisonNode]) => {
      if (!comparisonNode || typeof comparisonNode !== "object") {
        return;
      }

      const percentOfTarget =
        typeof comparisonNode.percentOfTarget === "number"
          ? comparisonNode.percentOfTarget
          : null;

      if (percentOfTarget == null || Number.isNaN(percentOfTarget)) {
        return;
      }

      const tileId = mapSnapshotNutrientIdToTileId(nutrientId);
      if (!tileId) {
        return;
      }

      if (!result[tileId]) {
        result[tileId] = {};
      }

      result[tileId][dateIso] = percentOfTarget;
    });
  });

  return result;
}

function buildDailyPercentDvFromCalculateSnapshot(snapshot) {
  const result = {};

  if (!snapshot || typeof snapshot !== "object") {
    return result;
  }

  const dailyNode = snapshot.dailyIntakeByDate;
  if (dailyNode && typeof dailyNode === "object") {
    Object.entries(dailyNode).forEach(([dateIso, dayValue]) => {
      if (!dayValue || typeof dayValue !== "object") {
        return;
      }

      const comparison =
        dayValue.intakeSummary?.comparisonVsTargets ||
        dayValue.comparisonVsTargets ||
        null;

      if (!comparison || typeof comparison !== "object") {
        return;
      }

      Object.entries(comparison).forEach(([nutrientId, comparisonNode]) => {
        if (!comparisonNode || typeof comparisonNode !== "object") {
          return;
        }

        const percentOfTarget =
          typeof comparisonNode.percentOfTarget === "number"
            ? comparisonNode.percentOfTarget
            : null;

        if (percentOfTarget == null || Number.isNaN(percentOfTarget)) {
          return;
        }

        const tileId = mapSnapshotNutrientIdToTileId(nutrientId);
        if (!tileId) {
          return;
        }

        if (!result[tileId]) {
          result[tileId] = {};
        }

        result[tileId][dateIso] = percentOfTarget;
      });
    });
  }

  return result;
}

function mergeDailyPercentDvMaps(primary, secondary) {
  const merged = { ...(primary || {}) };

  if (!secondary || typeof secondary !== "object") {
    return merged;
  }

  Object.entries(secondary).forEach(([nutrientId, series]) => {
    if (!series || typeof series !== "object") {
      return;
    }

    if (!merged[nutrientId]) {
      merged[nutrientId] = {};
    }

    merged[nutrientId] = {
      ...merged[nutrientId],
      ...series,
    };
  });

  return merged;
}

export default function MyNutritionStatus() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [range, setRange] = useState("today");
  const [chartView, setChartView] = useState("class");

  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const [hasAppliedProfileGate, setHasAppliedProfileGate] = useState(false);

  const [isDevJsonModalOpen, setIsDevJsonModalOpen] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  const [copiedPane, setCopiedPane] = useState(null);

  const [assistantMode, setAssistantMode] = useState(false);
  const [assistantHasRun, setAssistantHasRun] = useState(false);
  const [showBmiInfoModal, setShowBmiInfoModal] = useState(false);
  const [showPriorityInfoModal, setShowPriorityInfoModal] = useState(false);

  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const openSettingsFromHash = () => {
      if (window.location.hash === "#settings") {
        setIsUserSettingsOpen(true);
      }
    };

    openSettingsFromHash();
    window.addEventListener("hashchange", openSettingsFromHash);

    return () => {
      window.removeEventListener("hashchange", openSettingsFromHash);
    };
  }, []);

  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [dailyIntakeByDate, setDailyIntakeByDate] = useState(null);
  const [dailyIntakeLoading, setDailyIntakeLoading] = useState(true);

  const [foodSuggestions, setFoodSuggestions] = useState(null);

  const [clinicalTextByVitaminKey, setClinicalTextByVitaminKey] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadFoodAndClinicalFromCsv() {
      try {
        if (!sourcesCsvUrl) return;

        const response = await fetch(sourcesCsvUrl);
        if (!response.ok) return;

        const text = await response.text();
        const lines = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (lines.length <= 1) return;

        const suggestionMap = {};
        const clinicalMap = {};

        for (let index = 1; index < lines.length; index += 1) {
          const line = lines[index];
          const parts = line.split(";");
          if (parts.length < 4) continue;

          const vitaminNameRaw = (parts[0] || "").trim();
          const food = (parts[1] || "").trim();
          const serving = (parts[2] || "").trim();
          const approxDv = Number.parseFloat(parts[3]) || 0;
          const sideEffects = (parts[4] || "").trim();
          const symptoms = (parts[5] || "").trim();

          const vitaminKey = normaliseVitaminKeyFromLabel(vitaminNameRaw);
          if (!vitaminKey) {
            continue;
          }

          if (food && serving) {
            if (!suggestionMap[vitaminKey]) {
              suggestionMap[vitaminKey] = [];
            }

            suggestionMap[vitaminKey].push({
              food,
              serving,
              approxDv,
            });
          }

          if (!clinicalMap[vitaminKey]) {
            clinicalMap[vitaminKey] = {};
          }

          const clinicalEntry = clinicalMap[vitaminKey];

          if (sideEffects && !clinicalEntry.high) {
            clinicalEntry.high = sideEffects;
          }

          if (symptoms && !clinicalEntry.low) {
            clinicalEntry.low = symptoms;
          }
        }

        Object.keys(suggestionMap).forEach((vitaminKey) => {
          suggestionMap[vitaminKey].sort(
            (left, right) => (right.approxDv || 0) - (left.approxDv || 0),
          );
        });

        if (!cancelled) {
          setFoodSuggestions(suggestionMap);
          setClinicalTextByVitaminKey(clinicalMap);
        }
      } catch {
      }
    }

    loadFoodAndClinicalFromCsv();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadUserProfile() {
      if (!currentUser) {
        if (!cancelled) {
          setUserProfile(null);
          setProfileLoading(false);
        }
        return;
      }

      setProfileLoading(true);

      try {
        const reference = doc(db, "users", currentUser.uid);
        const snapshot = await getDoc(reference);

        if (!cancelled) {
          setUserProfile(snapshot.exists() ? snapshot.data() : null);
        }
      } catch {
        if (!cancelled) {
          setUserProfile(null);
        }
      } finally {
        if (!cancelled) {
          setProfileLoading(false);
        }
      }
    }

    loadUserProfile();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);


  useEffect(() => {

    if (profileLoading) {
      return;
    }


    if (hasAppliedProfileGate) {
      return;
    }

    const profileIsExplicitlyComplete =
      userProfile && userProfile.profileSetUpComplete === true;

    const profileIsMissingOrIncomplete = !profileIsExplicitlyComplete;

    if (profileIsMissingOrIncomplete) {

      setIsUserSettingsOpen(true);
      setHasAppliedProfileGate(true);
    }
  }, [profileLoading, userProfile, hasAppliedProfileGate]);

  useEffect(() => {
    let cancelled = false;

    async function loadDailyIntakeSnapshotsForRange() {
      if (!currentUser) {
        if (!cancelled) {
          setDailyIntakeByDate(null);
          setDailyIntakeLoading(false);
        }
        return;
      }

      const startIso = formatDateToIsoDateString(dateRange.start);
      const endIso = formatDateToIsoDateString(dateRange.end);

      if (!startIso || !endIso) {
        if (!cancelled) {
          setDailyIntakeByDate(null);
          setDailyIntakeLoading(false);
        }
        return;
      }

      setDailyIntakeLoading(true);

      try {
        const dailyIntakeCollectionReference = collection(
          db,
          "users",
          currentUser.uid,
          "dailyIntake",
        );

        const dailyIntakeQuery = query(
          dailyIntakeCollectionReference,
          where("dateIso", ">=", startIso),
          where("dateIso", "<=", endIso),
        );

        const querySnapshot = await getDocs(dailyIntakeQuery);
        const byDate = {};

        querySnapshot.forEach((documentSnapshot) => {
          const documentData = documentSnapshot.data();
          const dateIsoFromDocument =
            typeof documentData.dateIso === "string"
              ? documentData.dateIso
              : documentSnapshot.id;

          byDate[dateIsoFromDocument] = documentData;
        });

        if (!cancelled) {
          setDailyIntakeByDate(byDate);
        }
      } catch {
        if (!cancelled) {
          setDailyIntakeByDate(null);
        }
      } finally {
        if (!cancelled) {
          setDailyIntakeLoading(false);
        }
      }
    }

    loadDailyIntakeSnapshotsForRange();

    return () => {
      cancelled = true;
    };
  }, [currentUser, dateRange.start, dateRange.end]);

  const jsonToggleButtonLabel = isDevJsonModalOpen
    ? uiText?.hideJsonButtonLabel ?? "Hide JSON"
    : uiText?.showJsonButtonLabel ?? "Show JSON";

  const jsonToggleButtonTitle = isDevJsonModalOpen
    ? uiText?.hideJsonButtonTitle ?? "Hide JSON developer preview"
    : uiText?.showJsonButtonTitle ?? "Show JSON developer preview";

  const jsonToggleButtonAriaLabel = isDevJsonModalOpen
    ? uiText?.hideJsonButtonAriaLabel ?? "Hide JSON developer preview"
    : uiText?.showJsonButtonAriaLabel ?? "Show JSON developer preview";

  const devJsonModalTitle = uiText?.devJsonModalTitle ?? "Developer JSON preview";
  const devJsonModalCloseLabel = uiText?.devJsonModalCloseLabel ?? "Close";
  const devJsonModalCloseTitle = uiText?.devJsonModalCloseTitle ?? "Close JSON preview";
  const devJsonModalCloseAriaLabel =
    uiText?.devJsonModalCloseAriaLabel ?? "Close JSON preview";
  const devJsonLeftPaneTitle = uiText?.devJsonLeftPaneTitle ?? "UserFirebase JSON";
  const devJsonRightPaneTitle = uiText?.devJsonRightPaneTitle ?? "UseDaily JSON";
  const devJsonCalculatePaneTitle =
    uiText?.devJsonCalculatePaneTitle ?? "Nutrition Calculate JSON";

  const devJsonPayload = useMemo(
    () =>
      buildNutritionTargetsSettingsPreview({
        userProfile,
        dailyIntakeByDate,
      }),
    [userProfile, dailyIntakeByDate],
  );

  const useDailyJsonPayload = useMemo(
    () =>
      buildUseDailyPreviewFromNnr2023({
        userProfile,
        dailyIntakeByDate,
      }),
    [userProfile, dailyIntakeByDate],
  );

  const nutritionCalculateJsonPayload = useMemo(() => {
    const rangeStartIso = formatDateToIsoDateString(dateRange.start);
    const rangeEndIso = formatDateToIsoDateString(dateRange.end);

    const snapshot = buildNutritionTargetsCalculationsPreview({
      targetsSnapshot: devJsonPayload,
      useDailySnapshot: useDailyJsonPayload,
      activeDateIso: formatDateToIsoDateString(selectedDate),
      rangeKind: range,
      rangeStartDateIso: rangeStartIso,
      rangeEndDateIso: rangeEndIso,
    });

    return snapshot;
  }, [devJsonPayload, useDailyJsonPayload, selectedDate, range, dateRange.start, dateRange.end]);

  const nutritionOverridesFromCalculateSnapshot = useMemo(() => {
    if (!nutritionCalculateJsonPayload) {
      return null;
    }

    return buildNutritionDataOverridesFromCalculateSnapshot(
      nutritionCalculateJsonPayload,
    );
  }, [nutritionCalculateJsonPayload]);

  const fitnessBodyOverridesFromSnapshots = useMemo(() => {
    if (!devJsonPayload && !useDailyJsonPayload && !nutritionCalculateJsonPayload) {
      return null;
    }

    const bmiNode = buildBmiNodeFromSnapshots({
      userTargetsSettingsSnapshot: devJsonPayload,
      useDailySnapshot: useDailyJsonPayload,
      nutritionCalculateSnapshot: nutritionCalculateJsonPayload,
    });

    if (!bmiNode || !Number.isFinite(bmiNode.value)) {
      return null;
    }

    return {
      bmi: bmiNode,
    };
  }, [devJsonPayload, useDailyJsonPayload, nutritionCalculateJsonPayload]);

  const nutritionUserName = useMemo(() => {
    if (userProfile && typeof userProfile === "object") {
      if (typeof userProfile.fullName === "string" && userProfile.fullName.trim()) {
        return userProfile.fullName.trim();
      }

      const firstName =
        typeof userProfile.firstName === "string" ? userProfile.firstName.trim() : "";
      const lastName =
        typeof userProfile.lastName === "string" ? userProfile.lastName.trim() : "";
      const fullFromParts = `${firstName} ${lastName}`.trim();

      if (fullFromParts) {
        return fullFromParts;
      }
    }

    if (currentUser?.displayName && currentUser.displayName.trim()) {
      return currentUser.displayName.trim();
    }

    if (currentUser?.email && typeof currentUser.email === "string") {
      const local = currentUser.email.split("@")[0];
      if (local && local.trim()) {
        return local.trim();
      }
    }

    return "Valued Member";
  }, [userProfile, currentUser]);

  const useFirestoreNutritionArgs = {
    userId: currentUser?.uid ?? null,
    selectedDate,
    range,
    dateRange,
  };

  const { nutritionData, trial, loading } = useFirestoreNutrition(
    useFirestoreNutritionArgs,
  );

  const effectiveNutritionData = useMemo(() => {
    const baseData = nutritionData || {};
    const overrides = nutritionOverridesFromCalculateSnapshot;
    const fitnessOverrides = fitnessBodyOverridesFromSnapshots;

    const merged = { ...baseData };

    if (overrides && typeof overrides === "object") {
      Object.keys(overrides).forEach((nutrientId) => {
        const overrideNode = overrides[nutrientId];
        if (!overrideNode) {
          return;
        }

        const baseNode = merged[nutrientId] || {};
        merged[nutrientId] = {
          ...baseNode,
          ...overrideNode,
        };
      });
    }

    if (fitnessOverrides && typeof fitnessOverrides === "object") {
      Object.keys(fitnessOverrides).forEach((fitnessId) => {
        const overrideNode = fitnessOverrides[fitnessId];
        if (!overrideNode) {
          return;
        }

        const baseNode = merged[fitnessId] || {};
        merged[fitnessId] = {
          ...baseNode,
          ...overrideNode,
        };
      });
    }

    return merged;
  }, [nutritionData, nutritionOverridesFromCalculateSnapshot, fitnessBodyOverridesFromSnapshots]);

  const statuses = useMemo(
    () => getStatuses(effectiveNutritionData),
    [effectiveNutritionData],
  );

  const categoryOverview = useMemo(
    () => getCategoryOverview(effectiveNutritionData) || [],
    [effectiveNutritionData],
  );

  const groups = useMemo(
    () => getGroups(effectiveNutritionData) || [],
    [effectiveNutritionData],
  );

  const chartDataTransformed = useMemo(
    () => getChartData(effectiveNutritionData),
    [effectiveNutritionData],
  );

  const lowByCategory = useMemo(() => {
    const buckets = {
      vitamins: [],
      minerals: [],
      fats: [],
    };

    for (const option of allNutrientOptions) {
      if (!option || !option.id) continue;

      const status = statuses[option.id];
      if (status !== "low") continue;

      const category = getCategoryForId(option.id);
      if (category === "vitamins" || category === "minerals" || category === "fats") {
        buckets[category].push(option);
      }
    }

    Object.keys(buckets).forEach((key) => {
      buckets[key].sort((left, right) => left.label.localeCompare(right.label));
    });

    return buckets;
  }, [statuses]);

  const priorityItems = useMemo(
    () => [...lowByCategory.vitamins, ...lowByCategory.minerals, ...lowByCategory.fats],
    [lowByCategory],
  );

  const selectedRangeLabel = useMemo(
    () => getRangeLabel(range, dateRange.start, dateRange.end),
    [range, dateRange.start, dateRange.end],
  );

  const priorityInfoMessage = useMemo(
    () => buildPriorityInfoMessage(selectedRangeLabel),
    [selectedRangeLabel],
  );

  const priorityTitle = useMemo(
    () => `Priority nutrients: ${selectedRangeLabel}`,
    [selectedRangeLabel],
  );

  const hasDailyIntakeData =
    dailyIntakeByDate != null && Object.keys(dailyIntakeByDate).length > 0;
  const showNoDataNote = !hasDailyIntakeData;
  const noDataRangeLabel = selectedRangeLabel || "the selected dates";

  const daysInSelectedRange = useMemo(
    () => getInclusiveDaySpan(dateRange.start, dateRange.end),
    [dateRange.start, dateRange.end],
  );

  const handleLogMealClick = () => {
    const targetUrl = getAiAssistantUrl();
    if (targetUrl.startsWith("http")) {
      window.location.href = targetUrl;
    } else {
      navigate(targetUrl);
    }
  };

  const handleCopyJson = async () => {
    try {
      const jsonString = JSON.stringify(devJsonPayload, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 2000);
    } catch {
    }
  };

  const handleCopyPaneJson = async (pane, payload) => {
    try {
      const jsonString = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopiedPane(pane);
      setTimeout(() => setCopiedPane(null), 2000);
    } catch {
    }
  };

  const handleCalendarClick = () => {
    setAssistantMode(false);
    setAssistantHasRun(false);
    setShowDatePicker(true);
  };

  const handleDatePickerClose = () => {
    setShowDatePicker(false);
    setAssistantMode(false);
  };

  const handleDateRangeApply = ({ range: rangeType, startDate, endDate }) => {
    setDailyIntakeByDate(null);
    setDailyIntakeLoading(true);
    setRange(rangeType);
    setDateRange({ start: startDate, end: endDate });
    setSelectedDate(rangeType === "today" ? startDate : endDate);

    if (assistantMode) {
      setAssistantHasRun(true);
      setAssistantMode(false);
    }
  };

  const renderMetaIcon = () => (
    <CiMedicalClipboard size={35} className="icon-medical" aria-hidden="true" />
  );

  const recommendationFor = (id, label) => {
    const status = statuses[id];

    if (!nutritionCalculateJsonPayload || !foodSuggestions) {
      return null;
    }

    const intakeSummary = nutritionCalculateJsonPayload.intakeSummary;
    const comparisonMap = intakeSummary && intakeSummary.comparisonVsTargets;
    const comparison = comparisonMap && comparisonMap[id];

    const percentOfTarget =
      comparison && typeof comparison.percentOfTarget === "number"
        ? comparison.percentOfTarget
        : null;

    if (percentOfTarget != null && percentOfTarget > 100) {
      return null;
    }

    if (status !== "low" && status !== "data-needed") {
      return null;
    }

    const vitaminKey = normaliseVitaminKeyFromLabel(label);
    if (!vitaminKey) {
      return null;
    }

    const vitaminSuggestions = foodSuggestions[vitaminKey];
    if (!vitaminSuggestions || vitaminSuggestions.length === 0) {
      return null;
    }

    const maximumSuggestionsToShow = 3;
    const selectedSuggestions = vitaminSuggestions
      .slice(0, maximumSuggestionsToShow)
      .map((entry) => `${entry.food} ${entry.serving}`);

    if (selectedSuggestions.length === 0) {
      return null;
    }

    return `Food ideas: ${selectedSuggestions.join("; ")}`;
  };

  const renderHero = (hero) => {
    if (!hero) return null;

    if (hero.type === "ring") {
      return (
        <div className="hero-with-ring">
          <ProgressRing
            percentage={hero.pct ?? 0}
            ariaLabel={hero.ariaLabel || ""}
            stroke={hero.stroke}
          />
          <div>
            <div className="text-xl font-bold">{hero.mainText}</div>
            <div className="text-xs text-muted">{hero.subText}</div>
          </div>
        </div>
      );
    }

    if (hero.type === "value") {
      return (
        <div className="hero-with-ring">
          <ProgressRing
            percentage={typeof hero.value === "number" ? hero.value : 0}
            ariaLabel={`${hero.value}${hero.unit || ""}`}
            stroke="#10b981"
          />
          <div>
            <div className="text-xl font-bold">{hero.value}</div>
            {hero.unit ? (
              <div className="text-xs text-muted">{hero.unit}</div>
            ) : null}
          </div>
        </div>
      );
    }

    if (hero.type === "ratio") {
      return (
        <div className="hero-row">
          <span className="text-2xl font-bold">{hero.value}</span>
          <span className="text-xs text-muted">{hero.label}</span>
        </div>
      );
    }

    if (hero.type === "dualBars") {
      return (
        <div className="dual-bars">
          {hero.bars.map((bar) => (
            <div key={bar.key} className="bar-row">
              <span className="bar-label">{bar.label}</span>
              <div className="bar-track">
                <div
                  className={`bar-fill ${bar.className || ""}`}
                  style={{
                    width: `${Math.min(100, Math.max(0, bar.pct))}%`,
                  }}
                />
              </div>
              <span className="bar-pct">{Math.round(bar.pct)}%</span>
            </div>
          ))}
        </div>
      );
    }

    return <div className="text-sm text-muted">{hero.text}</div>;
  };

  const renderDetails = (details) => {
    if (!details) return null;

    if (details.type === "kvGrid") {
      return (
        <div className="details-grid">
          {details.rows.map((row) => (
            <div key={row.key}>
              {row.label}: {row.value}
            </div>
          ))}
        </div>
      );
    }

    if (details.type === "mutedText") {
      return <div className="text-sm text-muted">{details.text}</div>;
    }

    return null;
  };

  const dailyPercentFromFirestore = useMemo(
    () => buildDailyPercentDvFromDailySnapshots(dailyIntakeByDate),
    [dailyIntakeByDate],
  );

  const dailyPercentFromCalculate = useMemo(
    () => buildDailyPercentDvFromCalculateSnapshot(nutritionCalculateJsonPayload),
    [nutritionCalculateJsonPayload],
  );

  const combinedDailyPercentDvByNutrient = useMemo(
    () => mergeDailyPercentDvMaps(dailyPercentFromFirestore, dailyPercentFromCalculate),
    [dailyPercentFromFirestore, dailyPercentFromCalculate],
  );

  const nutrientTrendAlertsById = useMemo(() => {
    const analyzer = new NutrientTrendAnalyzer(combinedDailyPercentDvByNutrient);
    const alerts = analyzer.analyze();
    const byId = {};

    alerts.forEach((alert) => {
      const existing = byId[alert.nutrientId];
      if (!existing) {
        byId[alert.nutrientId] = alert;
        return;
      }

      const rank = {
        soft: 0,
        moderate: 1,
        high: 2,
      };
      const existingRank = rank[existing.severity] ?? 0;
      const incomingRank = rank[alert.severity] ?? 0;

      if (incomingRank > existingRank) {
        byId[alert.nutrientId] = alert;
      }
    });

    return byId;
  }, [combinedDailyPercentDvByNutrient]);

  const [selectedNutrientAlert, setSelectedNutrientAlert] = useState(null);

  const buildImmediateAlertForTile = (id) => {
    if (id === "potassiumSodium") {
      return null;
    }

    const node = effectiveNutritionData?.[id];
    if (!node || typeof node !== "object") {
      return null;
    }

    const percentOfTarget =
      typeof node.todayDV === "number" ? node.todayDV : null;

    const nutrientStatus = statuses?.[id] ?? null;
    const nutrientCategory = getCategoryForId(id);
    const isFatNutrient = nutrientCategory === "fats";
    const isVitaminOrMineral =
      nutrientCategory === "vitamins" || nutrientCategory === "minerals";

    let direction = null;
    let severity = "soft";

    if (percentOfTarget != null && !Number.isNaN(percentOfTarget)) {
      const extremeLowThreshold = DV_OK_LOW / 2;
      const extremeHighThreshold = DV_OK_HIGH * 2;

      if (percentOfTarget <= extremeLowThreshold) {
        direction = "low";
        severity = "moderate";
      } else if (percentOfTarget >= extremeHighThreshold) {
        direction = "high";
        severity = "moderate";
      }
    }

    if (!direction && (isFatNutrient || isVitaminOrMineral)) {
      if (nutrientStatus === "low") {
        direction = "low";
        severity = "soft";
      } else if (
        nutrientStatus === "high" ||
        nutrientStatus === "caution"
      ) {
        direction = "high";
        severity = "soft";
      }
    }

    if (!direction) {
      return null;
    }

    const hasNumericPercent =
      percentOfTarget != null && !Number.isNaN(percentOfTarget);
    const roundedPercent = hasNumericPercent
      ? Math.round(percentOfTarget)
      : null;

    const intensityPrefix = severity === "moderate" ? "very " : "";

    const ruleSummary =
      direction === "low"
        ? hasNumericPercent
          ? `For the selected dates, the intake is ${intensityPrefix}low at about ${roundedPercent}% of the daily value.`
          : `For the selected dates, the intake appears to be ${intensityPrefix}low.`
        : hasNumericPercent
          ? `For the selected dates, the intake is ${intensityPrefix}high at about ${roundedPercent}% of the daily value.`
          : `For the selected dates, the intake appears to be ${intensityPrefix}high.`;

    return {
      nutrientId: id,
      direction,
      windows: ["single-day-extreme"],
      ruleSummary,
      severity,
      kind: "extreme",
    };
  };

  const renderTile = (id, label) => {
    const spec = buildTileSpec({
      id,
      label,
      data: effectiveNutritionData,
      statuses,
    });

    if (!spec) return null;

    const badge = statusToBadge(spec.status);

    const trendAlert = nutrientTrendAlertsById[id] || null;
    const immediateAlert = buildImmediateAlertForTile(id);
    const alertForTile = trendAlert || immediateAlert;
    const isTrendAlert = Boolean(trendAlert && alertForTile === trendAlert);

    const meetsDateRangeRequirement =
      Number.isFinite(daysInSelectedRange) &&
      daysInSelectedRange >= MIN_DAYS_FOR_META_ICON;

    const hasAlert = Boolean(alertForTile) && meetsDateRangeRequirement;
    const metaIcon = hasAlert ? renderMetaIcon() : null;

    const handleMetaIconClick = (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (!alertForTile || !hasAlert) {
        return;
      }

      const vitaminKey = normaliseVitaminKeyFromLabel(label);
      const clinicalEntry =
        vitaminKey &&
          clinicalTextByVitaminKey &&
          clinicalTextByVitaminKey[vitaminKey]
          ? clinicalTextByVitaminKey[vitaminKey]
          : null;

      const clinicalTextForDirection =
        alertForTile.direction === "low"
          ? clinicalEntry?.low || ""
          : clinicalEntry?.high || "";

      const ruleSummaryText =
        alertForTile.ruleSummary && alertForTile.ruleSummary.trim()
          ? alertForTile.ruleSummary.trim()
          : "";

      const includeRuleSummary =
        isTrendAlert &&
        shouldIncludeRuleSummaryInMessage(alertForTile, daysInSelectedRange);

      let baseTrendSentence;

      if (isTrendAlert) {
        const windowDescription = describeTrendWindows(alertForTile.windows);

        const ruleSummarySuffix =
          includeRuleSummary && ruleSummaryText
            ? ` (${ruleSummaryText})`
            : "";

        baseTrendSentence =
          alertForTile.direction === "low"
            ? `Your logged intake of ${label} has been below the recommended level ${windowDescription}${ruleSummarySuffix}.`
            : `Your logged intake of ${label} has been consistently high or very high ${windowDescription}${ruleSummarySuffix}.`;
      } else {
        baseTrendSentence =
          alertForTile.direction === "low"
            ? `For the selected date, your logged intake of ${label} is much lower than the recommended level.`
            : `For the selected date, your logged intake of ${label} is much higher than the recommended level.`;
      }

      const clinicalSentence =
        alertForTile.direction === "low"
          ? clinicalTextForDirection
            ? ` Long-term low ${label} can cause symptoms such as ${clinicalTextForDirection}.`
            : ` Long-term low ${label} may lead to deficiency symptoms.`
          : clinicalTextForDirection
            ? ` Persistently high ${label} can increase the risk of side effects such as ${clinicalTextForDirection}.`
            : ` Persistently high ${label} may increase the risk of side effects or overload.`;

      const followUpSentence =
        " If you notice these or other symptoms, or if you are using supplements and have health concerns, consider reviewing this trend with a healthcare professional.";

      const message =
        baseTrendSentence +
        clinicalSentence +
        followUpSentence;

      setSelectedNutrientAlert({
        nutrientId: id,
        nutrientLabel: label,
        direction: alertForTile.direction,
        windows: alertForTile.windows,
        ruleSummary: alertForTile.ruleSummary,
        message,
        showClipboardExplanation: true,
      });
    };

    const helperOverride = recommendationFor(id, label);
    const detailsAlwaysExpanded = Boolean(spec.detailsAlwaysExpanded);
    const tileClassName = id === "bmi" ? "bmi-tile-card" : "";

    const handleBmiTitleIconClick = (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      setShowBmiInfoModal(true);
    };

    return (
      <Tile
        key={id}
        title={label}
        titleIcon={id === "bmi" ? <FcAnswers size={20} /> : undefined}
        onTitleIconClick={id === "bmi" ? handleBmiTitleIconClick : undefined}
        badge={badge}
        hero={renderHero(spec.hero)}
        helper={helperOverride ?? spec.helper ?? null}
        details={renderDetails(spec.details)}
        onOpen={undefined}
        onLog={undefined}
        metaIcon={hasAlert ? metaIcon : undefined}
        onMetaIconClick={hasAlert ? handleMetaIconClick : undefined}
        detailsAlwaysExpanded={detailsAlwaysExpanded}
        className={tileClassName}
      />
    );
  };

  const renderJsonToggleButton = () => {
    // When the flag is false, we render nothing but keep all logic intact.
    if (!IS_DEV_JSON_BUTTON_VISIBLE) {
      return null;
    }

    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary rounded-pill json-toggle-btn"
        onClick={() => setIsDevJsonModalOpen((previousValue) => !previousValue)}
        aria-pressed={isDevJsonModalOpen}
        aria-label={jsonToggleButtonAriaLabel}
        title={jsonToggleButtonTitle}
      >
        {jsonToggleButtonLabel}
      </button>
    );
  };

  const renderDevJsonModal = () => {
    // Optional: also respect the flag here so the modal never shows
    if (!IS_DEV_JSON_BUTTON_VISIBLE || !isDevJsonModalOpen) {
      return null;
    }

    return (
      <div className="dev-json-modal-backdrop" role="presentation">
        <div className="dev-json-modal" role="dialog" aria-modal="true" aria-label={devJsonModalTitle}>
          <Card>
            <CardContent>
              <div className="dev-json-modal-header">
                <h2 className="section-title dev-json-modal-title">{devJsonModalTitle}</h2>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill"
                    onClick={handleCopyJson}
                    aria-label={jsonCopied ? "JSON copied" : "Copy JSON to clipboard"}
                    title={jsonCopied ? "JSON copied!" : "Copy JSON to clipboard"}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "all 0.2s",
                    }}
                  >
                    {jsonCopied ? (
                      <>
                        <IoCheckmarkOutline size={16} />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <IoCopyOutline size={16} />
                        <span>Copy</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill dev-json-modal-close-btn"
                    onClick={() => setIsDevJsonModalOpen(false)}
                    aria-label={devJsonModalCloseAriaLabel}
                    title={devJsonModalCloseTitle}
                  >
                    {devJsonModalCloseLabel}
                  </button>
                </div>
              </div>

              <div
                className="dev-json-modal-preview"
                aria-label={uiText?.devJsonModalPreviewAriaLabel ?? "JSON preview area"}
              >
                <div className="dev-json-modal-column dev-json-modal-column-left">
                  <div className="dev-json-modal-column-header">
                    <span>{devJsonLeftPaneTitle}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleCopyPaneJson("left", devJsonPayload)}
                      aria-label={copiedPane === "left" ? "JSON copied" : "Copy JSON to clipboard"}
                      title={copiedPane === "left" ? "JSON copied!" : "Copy JSON to clipboard"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                      }}
                    >
                      {copiedPane === "left" ? (
                        <IoCheckmarkOutline size={14} />
                      ) : (
                        <IoCopyOutline size={14} />
                      )}
                    </button>
                  </div>
                  <div className="dev-json-modal-column-body">
                    <pre className="dev-json-modal-pre">
                      {JSON.stringify(devJsonPayload, null, 2)}
                    </pre>
                  </div>
                </div>

                <div className="dev-json-modal-column dev-json-modal-column-right">
                  <div className="dev-json-modal-column-header">
                    <span>{devJsonRightPaneTitle}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleCopyPaneJson("middle", useDailyJsonPayload)}
                      aria-label={copiedPane === "middle" ? "JSON copied" : "Copy JSON to clipboard"}
                      title={copiedPane === "middle" ? "JSON copied!" : "Copy JSON to clipboard"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                      }}
                    >
                      {copiedPane === "middle" ? (
                        <IoCheckmarkOutline size={14} />
                      ) : (
                        <IoCopyOutline size={14} />
                      )}
                    </button>
                  </div>
                  <div className="dev-json-modal-column-body">
                    <pre className="dev-json-modal-pre">
                      {JSON.stringify(useDailyJsonPayload, null, 2)}
                    </pre>
                  </div>
                </div>

                <div className="dev-json-modal-column dev-json-modal-column-calculate">
                  <div className="dev-json-modal-column-header">
                    <span>{devJsonCalculatePaneTitle}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        handleCopyPaneJson("right", nutritionCalculateJsonPayload)
                      }
                      aria-label={copiedPane === "right" ? "JSON copied" : "Copy JSON to clipboard"}
                      title={copiedPane === "right" ? "JSON copied!" : "Copy JSON to clipboard"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 8px",
                        fontSize: "12px",
                      }}
                    >
                      {copiedPane === "right" ? (
                        <IoCheckmarkOutline size={14} />
                      ) : (
                        <IoCopyOutline size={14} />
                      )}
                    </button>
                  </div>
                  <div className="dev-json-modal-column-body">
                    <pre className="dev-json-modal-pre">
                      {JSON.stringify(nutritionCalculateJsonPayload, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const handleAssistantClick = () => {
    setAssistantMode(true);
    setAssistantHasRun(false);
    setShowDatePicker(true);
  };

  if (loading || profileLoading || dailyIntakeLoading) {
    return (
      <div className="phn-nutrition">
        <div className="topbar">
          <div className="container topbar-content">
            <div className="topbar-left">
              <h1 className="dashboardtitle">{uiText.screenTitle}</h1>
              {renderJsonToggleButton()}
            </div>
          </div>
        </div>

        <main
          className="container main"
          style={{
            textAlign: "center",
            padding: "40px",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              color: "#666",
            }}
          >
            Loading nutrition data...
          </div>
        </main>

        {renderDevJsonModal()}
      </div>
    );
  }

  return (
    <div className="phn-nutrition">
      <div className="topbar">
        <div className="container topbar-content">
          <div className="topbar-left">
            <h1 className="dashboardtitle">{uiText.screenTitle}</h1>
            {renderJsonToggleButton()}

            {trial?.active && typeof trial?.daysLeft === "number" && (
              <div className="trial-row">
                <button
                  className="button upgrade-btn"
                  onClick={() => {
                    sessionStorage.setItem(SKIP_TRIAL_REDIRECT, "1");
                    navigate("/home");
                  }}
                  aria-label={uiText.upgradeAria}
                >
                  {uiText.upgradeCta}
                </button>
                <span className="trial-text">
                  {uiText.trialPrefix} {trial.daysLeft} {uiText.trialDays(trial.daysLeft)}
                </span>
              </div>
            )}
          </div>

          <div className="spacer" />

          <span className="date-label" aria-live="polite">
            {selectedRangeLabel}
          </span>

          <div className="date-controls">
            <button
              className="date-btn"
              onClick={handleCalendarClick}
              aria-label="Select date range"
              title={selectedRangeLabel}
            >
              <FcCalendar className="date-calendar-icon" />
            </button>

            <button
              type="button"
              className="date-qa-btn"
              onClick={() => setIsUserSettingsOpen(true)}
              aria-label={uiText.quickAddAria}
            >
              <FcEngineering className="date-engineering-icon" />
            </button>
          </div>
        </div>
      </div>

      <main className="container main">
        {showNoDataNote && (
          <section className="section" aria-live="polite">
            <Card className="no-data-card" aria-label="No data notice">
              <CardContent className="no-data-card-inner">
                <div className="no-data-icon" aria-hidden="true">
                  <span className="no-data-icon-mark">🌿</span>
                </div>
                <div className="no-data-text">
                  <p className="no-data-card-kicker">
                    Welcome back, {nutritionUserName}
                  </p>
                  <p className="no-data-card-title">
                    No nutrition data for {noDataRangeLabel} yet
                  </p>
                  <div className="no-data-card-actions">
                    <button
                      type="button"
                      className="btn btn-solid btn-sm"
                      onClick={handleLogMealClick}
                    >
                      Log a meal for today
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        <section className="section" aria-label="Insights">
          <h3 className="section-title">
            {uiText.insightsTitle}
            <button
              type="button"
              className="assistant-btn"
              aria-label="Assistant"
              onClick={handleAssistantClick}
            >
              <FcAssistant size={24} />
            </button>
          </h3>

          <div className="grid-insights">
            {assistantHasRun && (
              <Card>
                <CardContent>
                  <AiMyNutritionStatus
                    db={db}
                    nutritionTargetsCalculations={nutritionCalculateJsonPayload}
                    region="NORDIC"
                    userName={nutritionUserName}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <section className="section">
          <div className="section-header">
            <h2 className="section-title">{uiText.nutritionDistTitle}</h2>
            <span className="section-sub">{uiText.nutritionDistSub}</span>
          </div>

          <TwoRingDonutChart
            view={chartView}
            onViewChange={setChartView}
            chartData={chartDataTransformed}
          />
        </section>

        <section className="section">
          <div className="section-header">
            <h2 className="section-title">
              {priorityTitle}
              <button
                type="button"
                className="section-title-icon-button"
                onClick={() => setShowPriorityInfoModal(true)}
                aria-label="How to use the Priority nutrients section"
                title="How to use the Priority nutrients section"
              >
                <FcAnswers size={20} />
              </button>
            </h2>
          </div>

          {priorityItems.length > 0 ? (
            <div className="grid-focus">
              {priorityItems.map(({ id, label }) => (
                <div key={id}>{renderTile(id, label)}</div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">
              Everything looks on track for the selected dates.
            </p>
          )}
        </section>

        <section className="section" aria-label={uiText.categoryOverviewTitle}>
          <h3 className="section-title">{uiText.categoryOverviewTitle}</h3>

          <div className="grid-overview">
            {(categoryOverview || [])
              .filter((category) => category && category.id)
              .map((category) => {
                const rawMeta = category.meta || "";

                const [mainStatRaw, subStatRaw] = rawMeta.split("•");

                const mainStat = (mainStatRaw || rawMeta).trim();
                const subStat = (subStatRaw || "").trim();

                const statusKey = deriveCategoryOverviewStatusKey(
                  category,
                  mainStat,
                  subStat,
                  statuses,
                );

                const badgeMeta = statusToBadge(statusKey);
                const intent = badgeMeta?.intent || "muted";
                const intentClass = `overview-dot--intent-${intent}`;

                return (
                  <button
                    key={category.id}
                    type="button"
                    className="overview-card"
                  >
                    <div className="overview-header">
                      <span className="overview-label">{category.label}</span>
                      <div
                        className={`overview-dot dot-${category.id} ${intentClass}`}
                        aria-label={badgeMeta.label}
                      />
                    </div>
                    <div className="overview-body">
                      <span className="overview-stat" title={mainStat}>
                        {mainStat}
                      </span>
                      {subStat && (
                        <span className="overview-sub" title={subStat}>
                          {subStat}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
          </div>
        </section>

        <section className="section">
          {(groups || [])
            .filter(
              (group) =>
                group &&
                typeof group.name === "string" &&
                Array.isArray(group.items) &&
                group.items.length > 0,
            )
            .map((group) => (
              <div key={group.name} className="group-block">
                <div className="section-header">
                  <h3 className="section-title">{group.name}</h3>
                </div>

                <div className="grid-group">
                  {group.items
                    .filter((item) => item && item.id && item.label)
                    .map((item) => (
                      <div
                        key={item.id}
                        className={item.id === "bmi" ? "bmi-tile-grid-item" : undefined}
                      >
                        {renderTile(item.id, item.label)}
                      </div>
                    ))}
                </div>
              </div>
            ))}
        </section>
      </main>

      <AiUserSettings
        open={isUserSettingsOpen}
        onClose={() => setIsUserSettingsOpen(false)}
        initialProfile={userProfile}
        onSaveProfile={(updatedProfile) => {
          setUserProfile((previousProfile) => ({
            ...(previousProfile || {}),
            ...updatedProfile,
          }));
        }}
      />

      <DateRangePicker
        isOpen={showDatePicker}
        onClose={handleDatePickerClose}
        onApply={handleDateRangeApply}
        initialRange={range}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
      />

      <AlertModal
        open={Boolean(selectedNutrientAlert)}
        title={
          selectedNutrientAlert
            ? `${selectedNutrientAlert.nutrientLabel} – ${selectedNutrientAlert.direction === "high"
              ? "High intake pattern (toxicity risk)"
              : "Possible deficiency pattern"
            }`
            : ""
        }
        message={
          selectedNutrientAlert ? (
            <NutrientAlertModalBody
              nutrientLabel={selectedNutrientAlert.nutrientLabel}
              direction={selectedNutrientAlert.direction}
              message={selectedNutrientAlert.message}
              showClipboardExplanation={selectedNutrientAlert.showClipboardExplanation}
              minDaysForClipboard={MIN_DAYS_FOR_META_ICON}
            />
          ) : ""
        }
        variant={selectedNutrientAlert?.direction === "high" ? "warning" : "info"}
        primaryAction={{
          label: "Got it",
          onClick: () => setSelectedNutrientAlert(null),
        }}
        secondaryAction={null}
        onClose={() => setSelectedNutrientAlert(null)}
        requester={{
          entityId: currentUser?.uid ?? "anonymous",
        }}
        service="ui.nutrition.trendAlert"
      />

      <AlertModal
        open={showPriorityInfoModal}
        title="How to use the Priority nutrients section"
        message={priorityInfoMessage}
        variant="info"
        primaryAction={{
          label: "Got it",
          onClick: () => setShowPriorityInfoModal(false),
        }}
        secondaryAction={null}
        onClose={() => setShowPriorityInfoModal(false)}
        requester={{
          entityId: currentUser?.uid ?? "anonymous",
        }}
        service="ui.nutrition.priorityNutrientsInfo"
      />

      <AlertModal
        open={showBmiInfoModal}
        title="How the BMI & Body Fat tile supports weight loss"
        message={<BmiInfoAnswersheet />}
        variant="info"
        primaryAction={{
          label: "Got it",
          onClick: () => setShowBmiInfoModal(false),
        }}
        secondaryAction={null}
        onClose={() => setShowBmiInfoModal(false)}
        requester={{
          entityId: currentUser?.uid ?? "anonymous",
        }}
        service="ui.nutrition.bmiInfo"
      />

      {renderDevJsonModal()}
    </div>
  );
}
